import { FormType } from '../../../types/types';

// Social Button Corner Radius Styles (from Figma)
// Enum values are the corner radius in pixels
export enum ButtonStyle {
  RECTANGLE = 0, // Square corners
  ROUND_RECTANGLE = 4, // Slightly rounded corners
  PILL = 100, // Pill/circular shape (high value ensures full rounding)
}

// Provider Color Themes
export enum ProviderColorTheme {
  OUTLINE = 0, // White background, provider color text, border
  LIGHT = 1, // Light background variant
  DARK = 2, // Dark background variant
}

// SSO Button Types
export enum SSOButtonType {
  BUTTON = 0, // Full button styling (background, border)
  LINK = 1, // Text-only styling (no background/border)
}

// Font & Icon Size Configuration
export const FONT_ICON_SIZE_CONFIG = {
  MIN: 14,
  MAX: 20,
  DEFAULT: 16,
  UNIT: 'px',
} as const;

// Button Style Thumbnail Configuration
export const BUTTON_STYLE_THUMBNAIL_CONFIG = {
  SIZE: 51, // 51px x 51px per thumbnail (from existing pattern)
  GAP: 12, // 12px gap between thumbnails
} as const;

// Data Hooks for Testing
export const SOCIAL_DATA_HOOKS = {
  // Main Panel
  MAIN_PANEL: 'social-options-settings',
  BUTTON_STYLE_SELECTOR: 'social-button-style-selector',
  FONT_ICON_SIZE_SLIDER: 'social-font-icon-size-slider',

  // Button Style Options
  BUTTON_STYLES: {
    RECTANGLE: 'social-style-rectangle',
    ROUND_RECTANGLE: 'social-style-round-rectangle',
    PILL: 'social-style-pill',
  },

  // Provider Navigation
  PROVIDER_GOOGLE: 'social-provider-google',
  PROVIDER_FACEBOOK: 'social-provider-facebook',
  PROVIDER_SSO: 'social-provider-sso',

  // Reset Button
  RESET_DESIGN: 'social-reset-design',

  // Provider Panels (for future implementation)
  GOOGLE_PANEL: 'social-google-panel',
  FACEBOOK_PANEL: 'social-facebook-panel',
  SSO_PANEL: 'social-sso-panel',
} as const;

// Translation Key Structure
export const SOCIAL_LOGIN_TRANSLATION_KEYS = {
  MAIN_PANEL: {
    TITLE:
      'memberAuthentication.loginForm.settingsPanel.design.socialLoginOptions.title',
    BUTTON_STYLE:
      'memberAuthentication.loginForm.settingsPanel.design.socialLoginOptions.buttonStyle.title',
    FONT_ICON_SIZE:
      'memberAuthentication.loginForm.settingsPanel.design.socialLoginOptions.fontIconSize.title',
    ADDITIONAL_SETTINGS:
      'memberAuthentication.loginForm.settingsPanel.design.socialLoginOptions.additionalSettings.title',
    RESET_DESIGN:
      'memberAuthentication.loginForm.settingsPanel.design.socialLoginOptions.resetDesign.cta',
  },
  PROVIDERS_TITLE: {
    GOOGLE:
      'memberAuthentication.loginForm.settingsPanel.design.socialLoginOptions.providers.google.title',
    FACEBOOK:
      'memberAuthentication.loginForm.settingsPanel.design.socialLoginOptions.providers.facebook.title',
    SSO: 'memberAuthentication.loginForm.settingsPanel.design.socialLoginOptions.providers.sso.title',
  },
  PROVIDERS_BUTTON: {
    GOOGLE:
      'memberAuthentication.loginForm.settingsPanel.design.socialOptions.settings.moreDesignSettings.google.sectionTitle.googleButton',
    FACEBOOK:
      'memberAuthentication.loginForm.settingsPanel.design.socialOptions.settings.moreDesignSettings.facebook.sectionTitle.facebookButton',
    SSO: 'memberAuthentication.loginForm.settingsPanel.design.socialOptions.settings.moreDesignSettings.ssoButton.sectionTitle.ssoButton',
  },
  PROVIDER_COMMON: {
    COLOR_THEME_LABEL:
      'memberAuthentication.loginForm.settingsPanel.design.socialOptions.settings.moreDesignSettings.colorTheme.label',
    COLOR_THEME_OUTLINE:
      'memberAuthentication.loginForm.settingsPanel.design.socialOptions.settings.moreDesignSettings.colorTheme.option.outline',
    COLOR_THEME_LIGHT:
      'memberAuthentication.loginForm.settingsPanel.design.socialOptions.settings.moreDesignSettings.colorTheme.option.light',
    COLOR_THEME_DARK:
      'memberAuthentication.loginForm.settingsPanel.design.socialOptions.settings.moreDesignSettings.colorTheme.option.dark',
    BORDER_OPACITY_LABEL:
      'memberAuthentication.loginForm.settingsPanel.design.socialOptions.settings.moreDesignSettings.borderOpacity.label',
    TEXT_FONT_COLOR_LABEL:
      'memberAuthentication.loginForm.settingsPanel.design.socialOptions.settings.moreDesignSettings.ssoButton.textFontColor.label',
    RESET_DESIGN:
      'memberAuthentication.loginForm.settingsPanel.design.socialOptions.settings.moreDesignSettings.resetDesign.label',
    BACK_TO_DESIGN:
      'memberAuthentication.loginForm.settingsPanel.design.generalText.settings.backToDesign.cta',
  },
  SSO: {
    TABS_REGULAR:
      'memberAuthentication.loginForm.settingsPanel.design.socialOptions.settings.moreDesignSettings.ssoButton.tabs.regular',
    TABS_HOVER:
      'memberAuthentication.loginForm.settingsPanel.design.socialOptions.settings.moreDesignSettings.ssoButton.tabs.hover',
    BUTTON_TYPE_LABEL:
      'memberAuthentication.loginForm.settingsPanel.design.socialOptions.settings.moreDesignSettings.ssoButton.buttonType.label',
    BUTTON_TYPE_BUTTON:
      'memberAuthentication.loginForm.settingsPanel.design.socialOptions.settings.moreDesignSettings.ssoButton.buttonType.option.button',
    BUTTON_TYPE_LINK:
      'memberAuthentication.loginForm.settingsPanel.design.socialOptions.settings.moreDesignSettings.ssoButton.buttonType.option.link',
    SHOW_ICON_LABEL:
      'memberAuthentication.loginForm.settingsPanel.design.socialOptions.settings.moreDesignSettings.ssoButton.showIcon.label',
    TEXT_COLOR_LABEL:
      'memberAuthentication.loginForm.settingsPanel.design.socialOptions.settings.moreDesignSettings.sso.hover.textColor.label',
    FILL_OPACITY_COLOR:
      'memberAuthentication.loginForm.settingsPanel.design.socialOptions.settings.moreDesignSettings.ssoButton.fillOpacity.label',
    TEXT_ICON_COLOR_LABEL:
      'memberAuthentication.loginForm.settingsPanel.design.socialOptions.settings.moreDesignSettings.sso.hover.button.textIconColor.label',
    BORDER_OPACITY_COLOR:
      'memberAuthentication.loginForm.settingsPanel.design.socialOptions.settings.moreDesignSettings.ssoButton.borderOpacity.label',
  },
} as const;

export const SOCIAL_SIGNUP_TRANSLATION_KEYS = {
  MAIN_PANEL: {
    TITLE:
      'memberAuthentication.signupForm.settings.designTab.socialLoginOptions.title',
    BUTTON_STYLE:
      'memberAuthentication.signupForm.settings.designTab.socialLoginOptions.buttonStyle.label',
    FONT_ICON_SIZE:
      'memberAuthentication.signupForm.settings.designTab.socialLoginOptions.fontIconSize.label',
    ADDITIONAL_SETTINGS:
      'memberAuthentication.signupForm.settings.designTab.socialLoginOptions.additionalSettings.title',
    RESET_DESIGN:
      'memberAuthentication.signupForm.settings.designTab.resetDesign.button',
  },
  PROVIDERS_TITLE: {
    GOOGLE:
      'memberAuthentication.signupForm.settings.designTab.socialLoginOptions.additionalSettings.google',
    FACEBOOK:
      'memberAuthentication.signupForm.settings.designTab.socialLoginOptions.additionalSettings.facebook',
    SSO: 'memberAuthentication.signupForm.settings.designTab.socialLoginOptions.additionalSettings.sso',
  },
  PROVIDERS_BUTTON: {
    GOOGLE:
      'memberAuthentication.signupForm.settings.designTab.socialLoginOptions.googleButton.title',
    FACEBOOK:
      'memberAuthentication.signupForm.settings.designTab.socialLoginOptions.facebookButton.title',
    SSO: 'memberAuthentication.signupForm.settings.designTab.socialLoginOptions.ssoButton.title',
  },
  PROVIDER_COMMON: {
    COLOR_THEME_LABEL:
      'memberAuthentication.signupForm.settings.designTab.socialLoginOptions.colorTheme.label',
    COLOR_THEME_OUTLINE:
      'memberAuthentication.signupForm.settings.designTab.socialLoginOptions.colorTheme.option.outlined',
    COLOR_THEME_LIGHT:
      'memberAuthentication.signupForm.settings.designTab.socialLoginOptions.colorTheme.option.light',
    COLOR_THEME_DARK:
      'memberAuthentication.signupForm.settings.designTab.socialLoginOptions.colorTheme.option.dark',
    BORDER_OPACITY_LABEL:
      'memberAuthentication.signupForm.settings.designTab.borderOpacity.label',
    TEXT_FONT_COLOR_LABEL:
      'memberAuthentication.signupForm.settings.designTab.textfontcolor.label',
    RESET_DESIGN:
      'memberAuthentication.signupForm.settings.designTab.resetDesign.button',
    BACK_TO_DESIGN:
      'memberAuthentication.signupForm.settingsPanel.design.generalText.settings.backToDesign.cta',
  },
  SSO: {
    TABS_REGULAR:
      'memberAuthentication.signupForm.settings.designTab.socialLoginOptions.ssoButton.state.regular',
    TABS_HOVER:
      'memberAuthentication.signupForm.settings.designTab.socialLoginOptions.ssoButton.state.hover',
    BUTTON_TYPE_LABEL:
      'memberAuthentication.signupForm.settings.designTab.socialLoginOptions.ssoButton.buttonType.label',
    BUTTON_TYPE_BUTTON:
      'memberAuthentication.signupForm.settings.designTab.socialLoginOptions.ssoButton.buttonType.option.button',
    BUTTON_TYPE_LINK:
      'memberAuthentication.signupForm.settings.designTab.socialLoginOptions.ssoButton.buttonType.option.link',
    SHOW_ICON_LABEL:
      'memberAuthentication.signupForm.settings.designTab.socialLoginOptions.ssoButton.showIcon.label',
    TEXT_COLOR_LABEL:
      'memberAuthentication.signupForm.settingsPanel.design.socialOptions.settings.moreDesignSettings.sso.hover.textColor.label',
    FILL_OPACITY_COLOR:
      'memberAuthentication.signupForm.settingsPanel.design.socialOptions.settings.moreDesignSettings.ssoButton.fillOpacity.label',
    TEXT_ICON_COLOR_LABEL:
      'memberAuthentication.signupForm.settingsPanel.design.socialOptions.settings.moreDesignSettings.sso.hover.button.textIconColor.label',
    BORDER_OPACITY_COLOR:
      'memberAuthentication.signupForm.settingsPanel.design.socialOptions.settings.moreDesignSettings.ssoButton.borderOpacity.label',
  },
} as const;

// Button Style Configuration for Thumbnails
export interface ButtonStyleConfig {
  id: ButtonStyle;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  dataHook: string;
}

// Provider Configuration
export interface ProviderConfig {
  id: string;
  titleKey: string;
  dataHook: string;
}

export const getProviderConfigs = (
  formType: FormType,
): readonly ProviderConfig[] => {
  const translationKeys = getTranslationKeys(formType);
  return [
    {
      id: 'google',
      titleKey: translationKeys.PROVIDERS_TITLE.GOOGLE,
      dataHook: SOCIAL_DATA_HOOKS.PROVIDER_GOOGLE,
    },
    {
      id: 'facebook',
      titleKey: translationKeys.PROVIDERS_TITLE.FACEBOOK,
      dataHook: SOCIAL_DATA_HOOKS.PROVIDER_FACEBOOK,
    },
    {
      id: 'sso',
      titleKey: translationKeys.PROVIDERS_TITLE.SSO,
      dataHook: SOCIAL_DATA_HOOKS.PROVIDER_SSO,
    },
  ] as const;
};

export const getTranslationKeys = (formType: FormType) => {
  return formType === 'login'
    ? SOCIAL_LOGIN_TRANSLATION_KEYS
    : SOCIAL_SIGNUP_TRANSLATION_KEYS;
};
